import type { AutomationView, TileColumns, UiTheme } from '@adweb/shared';
import clsx from 'clsx';
import { Check, LayoutGrid, List } from 'lucide-react';

import { THEME_OPTIONS, TILE_COLUMN_OPTIONS, VIEW_OPTIONS } from './ui-preferences';

/**
 * Choix du style et de la vue des automatisations, partagé par la modale
 * utilisateur et la modale « affichage de l'organisation ».
 */
export function DisplayOptions({
  idPrefix,
  uiTheme,
  automationView,
  tileColumns,
  onThemeChange,
  onViewChange,
  onTileColumnsChange,
  disabled,
}: {
  idPrefix: string;
  uiTheme: UiTheme | null;
  automationView: AutomationView | null;
  tileColumns: TileColumns | null;
  onThemeChange: (value: UiTheme) => void;
  onViewChange: (value: AutomationView) => void;
  onTileColumnsChange: (value: TileColumns) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-5">
      <fieldset>
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Style
        </legend>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {THEME_OPTIONS.map((o) => {
            const selected = uiTheme === o.value;
            return (
              <label
                key={o.value}
                htmlFor={`${idPrefix}-theme-${o.value}`}
                className={clsx(
                  'relative flex cursor-pointer flex-col gap-2 rounded-lg border p-3 text-left transition focus-within:ring-2 focus-within:ring-brand/40',
                  selected
                    ? 'border-brand bg-brand-50/60 ring-1 ring-brand'
                    : 'border-slate-200 hover:border-slate-300',
                  disabled && 'pointer-events-none opacity-60',
                )}
              >
                <input
                  id={`${idPrefix}-theme-${o.value}`}
                  type="radio"
                  name={`${idPrefix}-theme`}
                  value={o.value}
                  checked={selected}
                  onChange={() => onThemeChange(o.value)}
                  disabled={disabled}
                  className="sr-only"
                />
                <span
                  className="flex h-8 overflow-hidden rounded-md ring-1 ring-slate-200"
                  aria-hidden="true"
                >
                  <span className="w-1/4" style={{ background: o.swatch[0] }} />
                  <span className="flex-1" style={{ background: o.swatch[1] }} />
                  <span className="w-2" style={{ background: o.swatch[2] }} />
                </span>
                <span className="flex items-center justify-between gap-2 text-sm font-semibold text-slate-900">
                  {o.label}
                  {selected && <Check className="h-4 w-4 text-brand" aria-hidden="true" />}
                </span>
                <span className="text-xs leading-snug text-slate-500">{o.description}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Présentation des automatisations
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {VIEW_OPTIONS.map((o) => {
            const selected = automationView === o.value;
            const Icon = o.value === 'LIST' ? List : LayoutGrid;
            return (
              <label
                key={o.value}
                htmlFor={`${idPrefix}-view-${o.value}`}
                className={clsx(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition focus-within:ring-2 focus-within:ring-brand/40',
                  selected
                    ? 'border-brand bg-brand-50/60 ring-1 ring-brand'
                    : 'border-slate-200 hover:border-slate-300',
                  disabled && 'pointer-events-none opacity-60',
                )}
              >
                <input
                  id={`${idPrefix}-view-${o.value}`}
                  type="radio"
                  name={`${idPrefix}-view`}
                  value={o.value}
                  checked={selected}
                  onChange={() => onViewChange(o.value)}
                  disabled={disabled}
                  className="sr-only"
                />
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{o.label}</span>
                  <span className="block text-xs leading-snug text-slate-500">{o.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Tuiles par ligne
        </legend>
        <div className="flex flex-wrap items-center gap-2">
          {TILE_COLUMN_OPTIONS.map((n) => {
            const selected = tileColumns === n;
            return (
              <label
                key={n}
                htmlFor={`${idPrefix}-cols-${n}`}
                className={clsx(
                  'flex h-9 min-w-[3rem] cursor-pointer items-center justify-center rounded-lg border px-3 text-sm font-semibold transition focus-within:ring-2 focus-within:ring-brand/40',
                  selected
                    ? 'border-brand bg-brand-50/60 text-brand-700 ring-1 ring-brand'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300',
                  disabled && 'pointer-events-none opacity-60',
                )}
              >
                <input
                  id={`${idPrefix}-cols-${n}`}
                  type="radio"
                  name={`${idPrefix}-cols`}
                  value={n}
                  checked={selected}
                  onChange={() => onTileColumnsChange(n)}
                  disabled={disabled}
                  className="sr-only"
                />
                {n}
              </label>
            );
          })}
          <span className="text-xs text-slate-500">
            Sur grand écran ; moins de colonnes sur un écran étroit.
          </span>
        </div>
      </fieldset>
    </div>
  );
}
